import { CommonModule, NgForOf } from "@angular/common";
import { Component, inject } from "@angular/core";
import printJS from "print-js";
import {
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiButton,
  TuiDataList,
  TuiDialogService,
  TuiDropdown,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBadgedContent,
  TuiDataListWrapper,
  TuiItemsWithMore,
  TuiTabs,
} from "@taiga-ui/kit";
import {
  TuiNavigation,
  TuiSearch,
} from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import { TuiInputModule, TuiTextfieldControllerModule } from "@taiga-ui/legacy";
import { NurseService } from "../../../core/services/nurse.service";
import { Location } from "@angular/common";
import { ForgotResetPasswordService } from "../../../core/services/forgot-reset-password.service.service";
import { SalaryService } from "../../../core/services/salary.service";
import { ShiftService } from "../../../core/services/shift.service";
import { getEmployeeNumber } from "../../../model/interface/nurse";

@Component({
  selector: 'app-single-salary',
  imports: [
    FormsModule,
    NgForOf,
    TuiButton,
    TuiDataListWrapper,
    TuiDataList,
    TuiDropdown,
    TuiTable,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    CommonModule,
    TuiBadgedContent,
    TuiInputModule,
    TuiTextfieldControllerModule
  ],
  templateUrl: './single-salary.component.html',
  styleUrl: './single-salary.component.scss'
})
export class SingleSalaryComponent {
  nurseId: string = "";
  month: number = 0;
  year: number = 0;
  nurse: any | null = null;
  getEmployeeDisplayNumber(): string {
    return getEmployeeNumber(this.nurse);
  }
  isLoading: boolean = false;
  salaryRecords: any[] = [];
  allMonthDates: Date[] = [];
  private readonly dialogs = inject(TuiDialogService);
  protected size: TuiSizeL | TuiSizeS = "s";
  protected open = false;

  constructor(
    private nurseService: NurseService,
    private router: Router,
    private route: ActivatedRoute,
    private resetPasswordService: ForgotResetPasswordService,
    private salaryService: SalaryService,
    private location: Location,
    private shiftService: ShiftService,
  ) {
  }

  ngOnInit() {
    this.nurseId = this.route.snapshot.paramMap.get("nurseId")!;
    this.month = parseInt(this.route.snapshot.paramMap.get("month")!, 10);
    this.year = parseInt(this.route.snapshot.paramMap.get("year")!, 10);
    if (this.nurseId) {
      this.loadNurseData();
    }
    if (this.month && this.year) {
      this.generateAllMonthDates();
      this.loadSalaryRecords();
    }
  }


  loadSalaryRecords(): void {
    this.isLoading = true;
    this.shiftService.getNurseShiftsBySpecificMonth(this.nurseId, this.year, this.month).subscribe((res: any) => {
      if (res?.success && res?.data) {
        this.salaryRecords = Array.isArray(res.data.shifts) ? res.data.shifts : [];
      } else {
        this.salaryRecords = [];
      }
      this.isLoading = false;
    });
  }
  
  

  loadNurseData(): void {
    this.isLoading = true;
    if (this.nurseId) {
      this.nurseService.getOneNurseById(this.nurseId).subscribe((res: any) => {
        console.log(res);
        this.nurse = res;
        this.isLoading = false;
      });
    }
  }
  
  getNetSalary(): number {
    if (!this.salaryRecords || this.salaryRecords.length === 0) {
      return 0;
    }
    return this.salaryRecords.reduce((total, shift) => total + shift.salary, 0);
  }
  
  getMonthName(month: number): string {
    const date = new Date(2000, month - 1);
    return date.toLocaleString('default', { month: 'long' });
  }
  

  private readonly alerts = inject(TuiAlertService);

  protected activeItemIndex = 0;

  protected onClick(item: string): void {
    this.alerts.open(item).subscribe();
  }

  goBack(): void {
    this.location.back();
  }

  exportPdf(): void {
    if (!this.nurse || !this.salaryRecords) {
      return;
    }

    const logoUrl = `${window.location.origin}/assets/logo-vertical.png`;
    const generatedDate = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const monthName = this.getMonthName(this.month);
    const netSalary = this.getNetSalary();

    const escapeHtml = (text: string): string => {
      if (!text) return "";
      return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    const formatDate = (dateString: any): string => {
      if (!dateString) return "—";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    };

    const rowsHtml = this.salaryRecords
      .map((shift) => {
        const clientName = shift.clientDetails?.name || "Unavailable";
        const shiftSalary = (shift.salary || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return `
        <tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 10px 12px; font-size: 12px; color: #1e293b;">${formatDate(shift.date)}</td>
          <td style="padding: 10px 12px; font-size: 12px; color: #1e293b;">${escapeHtml(clientName)}</td>
          <td style="padding: 10px 12px; font-size: 12px; color: #1e293b; text-align: center;">${shift.rooster}</td>
          <td style="padding: 10px 12px; font-size: 12px; color: #1e293b;">${escapeHtml(shift.mealOption || "—")}</td>
          <td style="padding: 10px 12px; font-size: 12px; color: #1e293b;">${escapeHtml(shift.shiftType || "—")}</td>
          <td style="padding: 10px 12px; font-size: 12px; color: #1e293b; text-align: right; font-weight: 500;">LKR ${shiftSalary}</td>
        </tr>`;
      })
      .join("");

    const regNoValue = getEmployeeNumber(this.nurse);
    const formattedNetSalaryNoDecimals = Math.round(netSalary).toLocaleString("en-US");

    const html = `
      <div style="font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;padding:8px 4px 24px;">
        <!-- Header -->
        <div style="display: flex; align-items: center; justify-content: space-between; position: relative; padding-bottom: 12px; border-bottom: 1px solid #cbd5e1;">
          <div style="width: 100px; display: flex; align-items: center; justify-content: flex-start;">
            <img src="${logoUrl}" alt="Royal Hospital Logo" style="height: 68px; width: auto; object-fit: contain;" />
          </div>
          <div style="flex: 1; text-align: center; line-height: 1.35;">
            <div style="font-size: 22px; font-weight: 800; color: #00b0ff; text-transform: uppercase; letter-spacing: 0.5px;">ROYAL HOSPITAL</div>
            <div style="font-size: 13px; font-weight: 800; color: #e91e63; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px;">HOME NURSING CARE</div>
            <div style="font-size: 10px; color: #475569; margin-top: 4px; font-weight: 500;">62 W A Silva Mawatha Colombo 06</div>
            <div style="font-size: 9px; color: #475569; margin-top: 2px; font-weight: 500;">Hot Line: 0713257499(WhatsApp), 0760656778, 0114000100</div>
          </div>
          <div style="width: 100px;"></div> <!-- Spacer to keep header text centered -->
        </div>

        <!-- Metadata Section -->
        <div style="margin: 24px 0; font-size: 12px; display: grid; grid-template-columns: 120px 1fr auto 120px; gap: 8px 16px; line-height: 1.4;">
          <div style="color: #64748b;">Reg No.</div>
          <div style="font-weight: 700; color: #0f172a;">${escapeHtml(regNoValue)}</div>
          <div style="color: #64748b; text-align: right;">Date</div>
          <div style="font-weight: 700; color: #0f172a; text-align: right;">${generatedDate}</div>
          
          <div style="color: #64748b;">Nurse Name</div>
          <div style="font-weight: 700; color: #0f172a; grid-column: span 3;">${escapeHtml(this.nurse.name)}</div>
        </div>

        <!-- Title -->
        <div style="font-size: 14px; font-weight: 700; color: #1e293b; margin: 28px 0; text-align: center; text-transform: capitalize;">
          ${monthName} – ${this.year} Pay Sheet
        </div>

        <!-- Pay Table -->
        <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
          <thead>
            <tr style="border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; background-color: #f8fafc;">
              <th style="text-align: left; padding: 8px 12px; font-size: 12px; font-weight: 600; color: #475569; width: 15%;">Date</th>
              <th style="text-align: left; padding: 8px 12px; font-size: 12px; font-weight: 600; color: #475569; width: 25%;">Client Name</th>
              <th style="text-align: center; padding: 8px 12px; font-size: 12px; font-weight: 600; color: #475569; width: 12%;">Roster</th>
              <th style="text-align: left; padding: 8px 12px; font-size: 12px; font-weight: 600; color: #475569; width: 18%;">Meal Option</th>
              <th style="text-align: left; padding: 8px 12px; font-size: 12px; font-weight: 600; color: #475569; width: 15%;">Shift Type</th>
              <th style="text-align: right; padding: 8px 12px; font-size: 12px; font-weight: 600; color: #475569; width: 15%;">Shift Salary</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <!-- Totals Section -->
        <div style="margin-top: 24px; display: flex; flex-direction: column; align-items: flex-end; font-size: 13px; color: #0f172a; line-height: 1.8;">
          <div style="display: grid; grid-template-columns: 140px 100px; text-align: right; gap: 4px 16px;">
            <div style="color: #475569; font-weight: 500; text-align: left;">Number Of Shifts</div>
            <div style="font-weight: 700; text-align: right;">${this.salaryRecords.length}</div>
            
            <div style="font-weight: 700; text-align: left;">Net Salary</div>
            <div style="font-weight: 800; text-align: right;">${formattedNetSalaryNoDecimals}LKR</div>
          </div>
        </div>
      </div>
    `;

    printJS({
      printable: html,
      type: "raw-html",
      documentTitle: " ",
      scanStyles: false,
      style: `
        @page { size: A4 portrait; margin: 15mm 12mm; }
        body { margin: 0; }
      `,
    });
  }

  getShiftStatusBgColor(status: string): string {
    switch (status) {
      case "Ongoing":
        return "#fd7e14";
      case "Upcoming":
        return "#007bff";
      case "Canceled":
        return "#dc3545";
      case "Completed":
        return "#6BC68A";
      case "Absent":
        return "#6c757d";
      default:
        return "gray";
    }
  }

  getServiceDurationColor(): string {
    if (!this.nurse || !this.nurse.registrationDate) {
      return 'gray';
    }
    const registrationDate = new Date(this.nurse.registrationDate);
    const now = new Date();
  
    let monthsDiff = (now.getFullYear() - registrationDate.getFullYear()) * 12;
    monthsDiff += now.getMonth() - registrationDate.getMonth();
  
    if (now.getDate() < registrationDate.getDate()) {
      monthsDiff--;
    }
  
    if (monthsDiff <= 3) {
      return '#87CEFA';
    } else if (monthsDiff <= 12) {
      return '#FFD700'; 
    } else {
      return '#90EE90'; 
    }
  }

  generateAllMonthDates(): void {
    this.allMonthDates = [];
    const daysInMonth = new Date(this.year, this.month, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      this.allMonthDates.push(new Date(this.year, this.month - 1, day));
    }
  }

  getShiftsForDate(date: Date): any[] {
    const targetYear = date.getFullYear();
    const targetMonth = date.getMonth() + 1; // getMonth() returns 0-11, so add 1
    const targetDay = date.getDate();
    
    return this.salaryRecords.filter(shift => {
      const shiftDate = new Date(shift.date);
      const shiftYear = shiftDate.getFullYear();
      const shiftMonth = shiftDate.getMonth() + 1; // getMonth() returns 0-11, so add 1
      const shiftDay = shiftDate.getDate();
      
      return shiftYear === targetYear && 
             shiftMonth === targetMonth && 
             shiftDay === targetDay;
    });
  }

  getShiftForDate(date: Date): any | null {
    const shifts = this.getShiftsForDate(date);
    return shifts.length > 0 ? shifts[0] : null;
  }

  isOffDay(date: Date): boolean {
    return this.getShiftsForDate(date).length === 0;
  }

  hasMultipleShifts(date: Date): boolean {
    return this.getShiftsForDate(date).length > 1;
  }

  getTotalSalaryForDate(date: Date): number {
    const shifts = this.getShiftsForDate(date);
    return shifts.reduce((total, shift) => total + (shift.salary || 0), 0);
  }

  
}